import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  effect,
  inject,
  input,
  inputBinding,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  TLocation,
  LocationSettings,
  ILocationSettings,
  UTILS,
  IUiLocationTag,
  PropExtend,
  IUiIconControlSettings,
  MODAL_SERVICE_TOKEN,
} from '@aksia/infrastructure';
import { PropsJoinPipe } from '../../../pipes/props-join.pipe';
import { NgTemplateOutlet } from '@angular/common';
import { LabelComponent } from '../../presentation/label/label.component';
import { IconComponent } from '../../presentation/icon/icon.component';
import { UiPopupDirective } from '../../../directives/ui/ui-popup.directive';
import { LocationService } from './location.service';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { from, mergeMap, of, toArray, EMPTY, Observable } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { TextComponent } from '../text/text.component';

@Component({
  selector: 'location',
  imports: [
    NgTemplateOutlet,
    LabelComponent,
    IconComponent,
    UiPopupDirective,
    TextComponent,
    ButtonComponent,
  ],
  templateUrl: '../dropdown/dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationComponent extends DropdownComponent {
  //#region Injections

  private locationService = inject(LocationService);
  private modalService = inject(MODAL_SERVICE_TOKEN);

  //#endregion

  //#region Inputs/Outputs

  override settings = input<LocationSettings, ILocationSettings>(
    new LocationSettings(),
    {
      transform: (settings: ILocationSettings) => this.initSettings(settings),
    },
  );

  //#endregion

  //#region Properties

  protected override formattedValue = computed(() =>
    UTILS.OBJECT.isNil(this.value())
      ? ''
      : this.propsJoinPipe.transform(
          this.value() as TLocation,
          this.settings().valueFormat?.split(',')!,
        ),
  );
  private readonly propsJoinPipe = new PropsJoinPipe();
  private toolMapRef: ComponentRef<ButtonComponent> | null = null;
  private map: google.maps.Map | null = null;
  private mapMarker: google.maps.marker.AdvancedMarkerElement | null = null;

  //#endregion

  //#region Functions

  constructor() {
    super();

    this.stateComparer = (a: TLocation, b: TLocation) =>
      a?.latitude === b?.latitude;

    afterNextRender(() => {
      let initMapFx = effect(
        () => {
          let locationServiceReady =
            this.locationService.locationServiceReady();

          if (locationServiceReady) {
            let value = this.value() as TLocation | undefined;
            if (value) {
              if (
                Number.isFinite(value.latitude) &&
                Number.isFinite(value.longitude)
              ) {
                if (this.map) {
                  this.updateMap();
                } else {
                  this.initMapTool();
                }
              } else {
                this.destroyMapTool();
              }
            } else {
              this.destroyMapTool();
            }
          }
        },
        { injector: this.injector },
      );
    });
  }

  protected override initSettings(settings: ILocationSettings) {
    let locationSettings = UTILS.OBJECT.assign(
      new LocationSettings(),
      settings,
    ) as LocationSettings;

    locationSettings.optionsAsyncFn = this.search;
    super.initSettings(locationSettings);

    return locationSettings;
  }

  protected search = <T>(query?: string) => {
    if (!query) return of([]);

    const token = new this.locationService.placesAPI.AutocompleteSessionToken();

    let googleAPIRequest: google.maps.places.AutocompleteRequest = {
      input: query,
      includedPrimaryTypes: [
        'street_address',
        'route',
        'country',
        'locality',
        'postal_code',
      ],
      sessionToken: token,
    };

    const suggestions$ = from(
      this.locationService.placesAPI.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        googleAPIRequest,
      ),
    );

    const results$ = suggestions$.pipe(
      // Flatten the suggestions array
      mergeMap((response) => from(response.suggestions || [])),
      // For each suggestion, fetch the detailed place information
      mergeMap((suggestion) => {
        const place = suggestion.placePrediction?.toPlace();
        return place ? of(place) : EMPTY;
      }),
      // Fetch address components for each place
      mergeMap((place) =>
        from(
          place
            .fetchFields({ fields: ['addressComponents', 'location'] })
            .then((res: any) => {
              let location: TLocation = {
                $uid: UTILS.GENERATE.nextuid,
              };
              return this.mapTagsToFields(res.place, location);
            }),
        ),
      ),
      // Collect all address components into an array
      toArray(),
    );
    return results$ as unknown as Observable<Array<T>>;
  };

  private mapTagsToFields(
    place: google.maps.places.Place,
    location: TLocation,
  ) {
    location.latitude = place?.location?.lat();
    location.longitude = place?.location?.lng();

    this.settings().fieldMap?.forEach((fieldMapItem) => {
      let locationField = fieldMapItem.field as keyof TLocation;

      fieldMapItem.tags.forEach((tag) => {
        if (
          place.addressComponents?.some(
            (component: google.maps.places.AddressComponent) =>
              component.types.includes(tag.tag),
          )
        ) {
          switch (tag.writeMode) {
            case 'append': {
              (location[locationField] as string) =
                `${location[locationField] ? location[locationField] + ' ' : ''}${place.addressComponents?.find((component: google.maps.places.AddressComponent) => component.types.includes(tag.tag))?.longText ?? ''}`;
              break;
            }
            case 'prepend': {
              (location[locationField] as string) =
                `${place.addressComponents?.find((component: google.maps.places.AddressComponent) => component.types.includes(tag.tag))?.longText ?? ''}${location[locationField] ?? ''}`;
              break;
            }
            default: {
              (location[locationField] as string) =
                place.addressComponents?.find(
                  (component: google.maps.places.AddressComponent) =>
                    component.types.includes(tag.tag),
                )?.longText ?? '';
              break;
            }
          }
        }
      });
    });
    location.$formattedValue = this.propsJoinPipe.transform(
      location as TLocation,
      this.settings().valueFormat?.split(',')!,
    ) as string;

    return location;
  }

  private initMapTool() {
    if (this.toolMapRef) return;
    let buttonSettings = signal<IUiIconControlSettings>({
      iconOnly: true,
      icon: this.ICONS.LOCATION,
    });
    this.toolMapRef = this.uiToolCodeRef.createComponent(ButtonComponent, {
      bindings: [inputBinding('settings', buttonSettings)],
    });
    this.toolMapRef.instance.hostElRef.nativeElement.onclick = () => {
      let modalCmpRef = this.modalService.openModal({
        label: 'Location',
        hasBackdrop: true,
        contentCssClass: 'w-[80vw] h-[80vh]',
        actions: [
          {
            label: 'Close [esc]',
            class: 'tertiary',
            action: () => modalCmpRef.instance.close(),
          },
        ],
        onModalOpen: () => {
          this.initMap(
            modalCmpRef.instance.hostElRef.nativeElement as HTMLElement,
          );
        },
      });
    };
    this.toolMapRef.instance.hostElRef.nativeElement.classList.add('unstyled');
    this.toolMapRef.instance.hostElRef.nativeElement.classList.add('xxs');
  }

  private destroyMapTool() {
    this.toolMapRef?.destroy();
    this.toolMapRef = null;
    this.map = null;
    this.mapMarker = null;
  }

  protected initMap(container: HTMLElement) {
    this.map = new google.maps.Map(
      container.querySelector('[uimodalcontent]') as HTMLElement,
      {
        center: {
          lat: (this.value() as TLocation).latitude!,
          lng: (this.value() as TLocation).longitude!,
        },
        zoom: 15,
        mapId: 'roadmap',
      },
    );
    this.mapMarker = new google.maps.marker.AdvancedMarkerElement({
      position: {
        lat: (this.value() as TLocation).latitude!,
        lng: (this.value() as TLocation).longitude!,
      },
      map: this.map,
      title: this.settings().markerLabel ?? 'Selected Location',
    });
  }

  private updateMap() {
    this.map!.setCenter({
      lat: (this.value() as TLocation).latitude!,
      lng: (this.value() as TLocation).longitude!,
    });

    this.mapMarker!.position = {
      lat: (this.value() as TLocation).latitude!,
      lng: (this.value() as TLocation).longitude!,
    };
  }

  //#endregion
}
